import React, {useCallback, useState} from 'react';
import {
  IconButton,
  Tooltip,
  Popover,
  List,
  Divider,
  Button,
  Box,
  Badge
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import {useTranslation} from 'react-i18next';
import {JoinedRoomData} from '@/redux-store/slices/matrix/room-data';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import AllChatsDialog from './all-chats-dialog';
import MatrixUnreadEventsProvider from '@/contexts/matrix/unread-events/provider';
import ChatItem from './chat-item';
import {useMatrixContext} from '@/contexts/matrix/client';

interface ChatMenuProps {
  chats: JoinedRoomData[];
  anchorEl: HTMLElement | null;
  onOpenChatMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseChatMenu: () => void;
}

const ChatMenu: React.FC<ChatMenuProps> = ({
  chats,
  anchorEl,
  onOpenChatMenu,
  onCloseChatMenu
}) => {
  const {t} = useTranslation();
  const [allChatsDialogOpen, setAllChatsDialogOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState<JoinedRoomData | null>(null);
  const {unreadTotal} = useMatrixContext();

  const open = Boolean(anchorEl);
  const id = open ? 'chat-popover' : undefined;

  const handleOpenAllChatsDialog = useCallback(() => {
    setAllChatsDialogOpen(true);
    onCloseChatMenu();
  }, [onCloseChatMenu]);

  const handleCloseAllChatsDialog = useCallback(() => {
    setAllChatsDialogOpen(false);
  }, []);

  const handleSelectChat = useCallback(
    (chat: JoinedRoomData) => {
      setSelectedChat(chat);
      onCloseChatMenu();
    },
    [onCloseChatMenu]
  );

  const handleCloseChatWindow = useCallback(() => {
    setSelectedChat(null);
  }, []);

  return (
    <>
      <Tooltip title={t('open_chats')}>
        <IconButton onClick={onOpenChatMenu}>
          <Badge
            badgeContent={unreadTotal}
            color="primary"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <ChatIcon
              fontSize="large"
              sx={{
                color: (theme) =>
                  theme.palette.customColors?.responsiveAppBarSettings,
                marginTop: 0.5,
                marginRight: {xs: 0.5, sm: 1},
                width: {xs: '30px', sm: '40px'},
                height: {xs: '30px', sm: '40px'}
              }}
            />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onCloseChatMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <Box sx={{p: 1, maxWidth: 400}}>
          {chats.length === 0 ? (
            <ResponsiveTypography>{t('no_chats')}</ResponsiveTypography>
          ) : (
            <List>
              {chats.slice(0, 5).map((chat, index) => (
                <MatrixUnreadEventsProvider
                  roomId={chat.roomId}
                  key={chat.roomId}
                >
                  <React.Fragment key={chat.roomId}>
                    <ChatItem
                      chat={chat}
                      onSelectChat={handleSelectChat}
                      notOpenChat={true}
                    />
                    {index < chats.slice(0, 5).length - 1 && <Divider />}
                  </React.Fragment>
                </MatrixUnreadEventsProvider>
              ))}
              {chats.length > 5 && (
                <>
                  <Divider />
                  <Box
                    sx={{
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.action.hover,
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <Button
                      fullWidth
                      onClick={handleOpenAllChatsDialog}
                      sx={{mt: 1}}
                    >
                      {t('all_chats')}
                    </Button>
                  </Box>
                </>
              )}
            </List>
          )}
        </Box>
      </Popover>
      <AllChatsDialog
        open={allChatsDialogOpen}
        onClose={handleCloseAllChatsDialog}
        chats={chats}
      />
      {selectedChat && (
        <ChatItem
          chat={selectedChat}
          onWindowClose={handleCloseChatWindow}
          shouldOpenDirectly={true}
        />
      )}
    </>
  );
};

export default ChatMenu;
